import { useState, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import '../styles/MarkdownEditor.css'

function MarkdownEditor({ value, onChange, placeholder, height = '400px' }) {
  const [mode, setMode] = useState('split') // 'edit', 'preview', 'split'
  const [content, setContent] = useState(value || '')

  useEffect(() => {
    setContent(value || '')
  }, [value])

  const handleChange = (e) => {
    const newValue = e.target.value
    setContent(newValue)
    if (onChange) {
      onChange(newValue)
    }
  }

  const insertMarkdown = (before, after = '') => {
    const textarea = document.querySelector('.markdown-textarea')
    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const selectedText = content.substring(start, end)
    const newText = content.substring(0, start) + before + selectedText + after + content.substring(end)
    setContent(newText)
    if (onChange) {
      onChange(newText)
    }
    // Restore cursor position
    setTimeout(() => {
      textarea.focus()
      textarea.setSelectionRange(start + before.length, start + before.length + selectedText.length)
    }, 0)
  }

  const toolbar = [
    { label: 'B', title: 'Bold', action: () => insertMarkdown('**', '**') },
    { label: 'I', title: 'Italic', action: () => insertMarkdown('*', '*') },
    { label: 'H1', title: 'Heading 1', action: () => insertMarkdown('# ') },
    { label: 'H2', title: 'Heading 2', action: () => insertMarkdown('## ') },
    { label: 'H3', title: 'Heading 3', action: () => insertMarkdown('### ') },
    { label: '•', title: 'Bullet List', action: () => insertMarkdown('- ') },
    { label: '1.', title: 'Numbered List', action: () => insertMarkdown('1. ') },
    { label: '</>', title: 'Code', action: () => insertMarkdown('`', '`') },
    { label: '""', title: 'Quote', action: () => insertMarkdown('> ') },
    { label: '🔗', title: 'Link', action: () => insertMarkdown('[', '](url)') },
    { label: '🖼️', title: 'Image', action: () => insertMarkdown('![alt](', ')') },
  ]

  return (
    <div className="markdown-editor-container">
      <div className="markdown-toolbar">
        <div className="toolbar-group">
          {toolbar.map((item, index) => (
            <button
              key={index}
              className="toolbar-button"
              onClick={item.action}
              title={item.title}
              type="button"
            >
              {item.label}
            </button>
          ))}
        </div>
        <div className="toolbar-group">
          <button
            className={`mode-button ${mode === 'edit' ? 'active' : ''}`}
            onClick={() => setMode('edit')}
            title="Edit mode"
            type="button"
          >
            ✏️ Edit
          </button>
          <button
            className={`mode-button ${mode === 'split' ? 'active' : ''}`}
            onClick={() => setMode('split')}
            title="Split mode"
            type="button"
          >
            ⚌ Split
          </button>
          <button
            className={`mode-button ${mode === 'preview' ? 'active' : ''}`}
            onClick={() => setMode('preview')}
            title="Preview mode"
            type="button"
          >
            👁️ Preview
          </button>
        </div>
      </div>

      <div className={`markdown-content mode-${mode}`} style={{ height }}>
        {(mode === 'edit' || mode === 'split') && (
          <div className="markdown-editor-pane">
            <textarea
              className="markdown-textarea"
              value={content}
              onChange={handleChange}
              placeholder={placeholder || 'Write your content in markdown...'}
              spellCheck="true"
            />
          </div>
        )}

        {(mode === 'preview' || mode === 'split') && (
          <div className="markdown-preview-pane">
            <div className="markdown-preview">
              {content ? (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content}
                </ReactMarkdown>
              ) : (
                <div className="preview-placeholder">
                  Preview will appear here...
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="markdown-footer">
        <span className="character-count">
          {content.length} characters
        </span>
        <a
          href="https://www.markdownguide.org/basic-syntax/"
          target="_blank"
          rel="noopener noreferrer"
          className="markdown-help-link"
        >
          Markdown Guide
        </a>
      </div>
    </div>
  )
}

export default MarkdownEditor
